import React, { useMemo } from "react";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import { scaleBand, scaleLinear } from "@visx/scale";
import { BarRounded } from "@visx/shape";
import { TooltipWithBounds, useTooltip } from "@visx/tooltip";
import { AXIS_LABEL, TOOLTIP_STYLE } from "./chartTheme";
import useArrowFocus from "./useArrowFocus";
import useHideOnEscape from "./useHideOnEscape";

// Barras verticais por categoria, num so eixo (spec do RPAA-286, decisao 12). Cada barra tem no
// maximo 24 px, 4 px arredondados so na ponta e nunca na base, e um nome que o leitor de ecra le
// ("Mon: 6"). A zona que reage ao rato e a banda inteira, maior do que a barra, e e o primeiro filho
// de cada marca: o anel de foco desenha-se nela (index.css). Uma categoria a zero fica sem barra mas
// continua focavel, para o teclado nao saltar dias. Os eixos e a grelha ficam fora da arvore de
// acessibilidade: as marcas ja dizem o nome e o valor. A `band` pinta uma faixa atras de um intervalo
// de categorias seguidas (o horario de servico no grafico por hora, spec do redesign R7). O svg nao
// corta o que passa das bordas (`overflow: visible`): metade do traco do anel fica fora do retangulo
// de toque.
// Tres coisas por barra, para a evolucao de um dia hora a hora (RPAA-286, item 12): o `previous` e o valor
// do periodo anterior na mesma categoria, desenhado como um contorno por cima da barra, com a forma dela (na
// mesma largura, os cantos de cima arredondados nos mesmos 4 px e aberto na base, para nada pintar por cima
// da base quadrada da barra), com o nome dado pelo `previousLabel` ("Ontem"); o `partial` pinta a barra mais
// tenue, para a hora que ainda decorre, e o nome da marca di-lo com o `partialLabel` ("a decorrer"); e um
// `value` a `null` e uma hora por chegar, sem barra (so o contorno, se o anterior o tem). O eixo do valor
// serve o maior dos dois periodos, sem contar os `null`.
// O contorno leva por baixo um halo da cor do cartao, mais largo do que ele: onde ontem fica abaixo de hoje
// o contorno corta a barra, e o cinzento sozinho sobre o azul quase nao se via (1,7:1 no claro, 1,3:1 no
// escuro). Com o halo o contorno le-se contra o cartao, e ai o `--chart-muted`, um token de antes deste
// item, da 2,5:1 no claro (3,4:1 no escuro): ainda abaixo dos 3:1 de um elemento grafico.
// A barra da hora a decorrer, a meia opacidade, da 2,0:1 contra o cartao no claro (2,1:1 no escuro); um
// traco de 1,5 px na cor dela, a cheio, contorna-a a 4,4:1 (4,6:1 no escuro).
const MARGIN = { top: 8, right: 8, bottom: 28, left: 44 };
const MAX_BAR = 24;
const RADIUS = 4;

// O contorno de ontem e o seu halo: uma barra com os cantos de cima arredondados e sem o lado de baixo, que
// sobe da base pela esquerda e desce pela direita. O raio nunca passa de metade do lado mais curto, como no
// BarRounded.
const openBarPath = ({ x, top, width, base }) => {
  const radius = Math.min(RADIUS, width / 2, (base - top) / 2);
  return `M${x},${base}V${top + radius}a${radius},${radius} 0 0 1 ${radius},${-radius}h${width - 2 * radius}a${radius},${radius} 0 0 1 ${radius},${radius}V${base}`;
};

// Os rotulos do eixo de baixo nunca se tocam. Cada um fica numa linha enquanto ha pelo menos LABEL_GAP
// px entre ele e os vizinhos. Quando dois se tocariam, os que nao cabem na sua banda partem em duas
// linhas num espaco; se ainda assim se tocam, o eixo mostra um rotulo sim, um nao, e o que nem assim
// cabe fica so na tooltip e na tabela. Medem-se no tipo do eixo (11 px, e o Arial que o visx poe nos
// eixos) com o measureText de um canvas, porque uma estimativa por caracter mudava os rotulos que hoje
// cabem a 1440 px.
const AXIS_FONT = "11px Arial";
const LABEL_GAP = 4;
const LINE_HEIGHT = 13;
let measureContext = null;
const textWidth = (text) => {
  if (!measureContext) measureContext = document.createElement("canvas").getContext("2d");
  measureContext.font = AXIS_FONT;
  return measureContext.measureText(text).width;
};
const widthOf = (lines) => Math.max(...lines.map(textWidth));

// As duas linhas de um rotulo, partido no espaco que deixa a linha mais comprida mais curta (num empate,
// a primeira linha fica a maior). Um numero nunca se separa da palavra que o segue ("5 min", "1 hora").
// Sem onde partir fica numa linha.
const twoLines = (label) => {
  let best = [label];
  for (let at = label.indexOf(" "); at >= 0; at = label.indexOf(" ", at + 1)) {
    if (/\d$/.test(label.slice(0, at))) continue;
    const lines = [label.slice(0, at), label.slice(at + 1)];
    if (best.length === 1 || widthOf(lines) <= widthOf(best)) best = lines;
  }
  return best;
};

// Os rotulos que o eixo mostra, cada um com a chave da sua categoria e as suas linhas.
const axisLabels = (data, step, tickEvery) => {
  const attempt = (every, wrap) =>
    data
      .filter((_, index) => index % every === 0)
      .map((d) => ({ key: d.key, lines: wrap && textWidth(d.label) > step * every - LABEL_GAP ? twoLines(d.label) : [d.label] }));
  const apart = (labels, every) => labels.every((label, index) => index === 0 || (widthOf(labels[index - 1].lines) + widthOf(label.lines)) / 2 + LABEL_GAP <= step * every);
  for (const every of [tickEvery, 2 * tickEvery]) {
    for (const wrap of [false, true]) {
      const labels = attempt(every, wrap);
      if (apart(labels, every)) return labels;
    }
  }
  // Um rotulo que nao passa da sua banda nunca toca outro assim: os que passam ficam so na tooltip e na tabela.
  return attempt(2 * tickEvery, true).filter((label) => widthOf(label.lines) <= 2 * tickEvery * step - LABEL_GAP);
};

// Um rotulo do eixo, numa ou em duas linhas. A primeira linha nao tem `dy` proprio, para ficar com o do
// eixo (o visx desce os rotulos de baixo 0.25em).
const AxisLabel = ({ lines, x, y, ...props }) => (
  <text x={x} y={y} {...props}>
    {lines.map((line, index) => (
      <tspan key={index} x={x} dy={index === 0 ? undefined : LINE_HEIGHT}>
        {line}
      </tspan>
    ))}
  </text>
);

const BarChart = ({ data, width, height = 200, colorOf, formatValue, formatTick, tickEvery = 1, label, band, previousLabel, partialLabel }) => {
  const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const x = scaleBand({ domain: data.map((d) => d.key), range: [0, innerWidth], padding: 0.2 });
  const step = x.step();
  const shownLabels = useMemo(() => axisLabels(data, step, tickEvery), [data, step, tickEvery]);
  const linesOf = Object.fromEntries(shownLabels.map((shown) => [shown.key, shown.lines]));
  // Um rotulo em duas linhas pede mais uma linha por baixo do eixo, tirada a altura das barras.
  const bottom = MARGIN.bottom + (shownLabels.some((shown) => shown.lines.length > 1) ? LINE_HEIGHT : 0);
  const innerHeight = height - MARGIN.top - bottom;
  const y = scaleLinear({ domain: [0, Math.max(1, ...data.flatMap((d) => [d.value, d.previous]).filter((value) => value != null))], range: [innerHeight, 0], nice: true });
  // Sao contagens: so graduacoes inteiras no eixo e na grelha (com maximo 1 nao ha 0,5).
  const ticks = y.ticks(4).filter(Number.isInteger);
  const barWidth = Math.min(MAX_BAR, x.bandwidth());
  const { tooltipOpen, tooltipData, tooltipLeft, tooltipTop, showTooltip, hideTooltip } = useTooltip();
  const { active, setActive, refFor, onKeyDown } = useArrowFocus(data.length);
  useHideOnEscape(tooltipOpen, hideTooltip);

  const show = (d, index) => {
    setActive(index);
    showTooltip({
      tooltipData: d,
      tooltipLeft: MARGIN.left + x(d.key) + x.bandwidth() / 2,
      tooltipTop: MARGIN.top + y(d.value ?? d.previous ?? 0),
    });
  };

  // O nome de uma marca: "16h: 12", e com o anterior "16h: 12, Ontem: 9"; o da hora a decorrer leva o
  // `partialLabel` ("17h: 3, a decorrer, Ontem: 8"), e uma hora por chegar so tem o anterior.
  const nameOf = (d) => {
    let own = d.value == null ? d.label : `${d.label}: ${formatValue(d.value)}`;
    if (d.partial) own = `${own}, ${partialLabel}`;
    return d.previous == null ? own : `${own}, ${previousLabel}: ${formatValue(d.previous)}`;
  };

  return (
    <div className="relative">
      <svg width={width} height={height} role="group" aria-label={label} style={{ overflow: "visible" }}>
        <Group left={MARGIN.left} top={MARGIN.top}>
          {band && band.keys.length > 0 && (
            <rect
              aria-hidden="true"
              data-testid="chart-band"
              x={x(band.keys[0])}
              y={0}
              width={x(band.keys[band.keys.length - 1]) + x.bandwidth() - x(band.keys[0])}
              height={innerHeight}
              fill="var(--chart-band)"
            />
          )}
          <g aria-hidden="true">
            <GridRows scale={y} width={innerWidth} tickValues={ticks} stroke="var(--chart-grid)" />
          </g>
          {data.map((d, index) => {
            const barX = x(d.key) + (x.bandwidth() - barWidth) / 2;
            const barHeight = d.value == null ? 0 : innerHeight - y(d.value);
            const previousTop = y(d.previous);
            const previousPath = d.previous > 0 && openBarPath({ x: barX, top: previousTop, width: barWidth, base: innerHeight });
            return (
              <g
                key={d.key}
                ref={refFor(index)}
                data-chart-mark=""
                tabIndex={index === active ? 0 : -1}
                role="img"
                aria-label={nameOf(d)}
                onFocus={() => show(d, index)}
                onBlur={hideTooltip}
                onMouseEnter={() => show(d, index)}
                onMouseLeave={hideTooltip}
                onKeyDown={(event) => onKeyDown(index, event)}
              >
                <rect x={x(d.key)} y={0} width={x.bandwidth()} height={innerHeight} fill="transparent" />
                {barHeight > 0 && (
                  <BarRounded
                    x={barX}
                    y={y(d.value)}
                    width={barWidth}
                    height={barHeight}
                    radius={RADIUS}
                    top
                    fill={colorOf(d.key)}
                    data-partial={d.partial ? "" : undefined}
                    fillOpacity={d.partial ? 0.5 : undefined}
                    stroke={d.partial ? colorOf(d.key) : undefined}
                    strokeWidth={d.partial ? 1.5 : undefined}
                  />
                )}
                {/* O topo vai tambem num atributo, para o teste o ler sem decifrar o caminho. */}
                {previousPath && (
                  <>
                    <path data-previous-halo="" d={previousPath} fill="none" stroke="var(--color-surface)" strokeWidth={3.5} />
                    <path data-previous="" data-top={previousTop} d={previousPath} fill="none" stroke="var(--chart-muted)" strokeWidth={1.5} />
                  </>
                )}
              </g>
            );
          })}
          <g aria-hidden="true">
            <AxisLeft
              scale={y}
              tickValues={ticks}
              tickFormat={formatTick}
              stroke="var(--chart-grid)"
              tickStroke="var(--chart-grid)"
              tickLabelProps={{ ...AXIS_LABEL, textAnchor: "end", dx: -4, dy: 3 }}
            />
            <AxisBottom
              top={innerHeight}
              scale={x}
              tickValues={shownLabels.map((shown) => shown.key)}
              tickFormat={(key) => key}
              tickComponent={({ formattedValue, ...props }) => <AxisLabel lines={linesOf[formattedValue]} {...props} />}
              stroke="var(--chart-grid)"
              tickStroke="var(--chart-grid)"
              tickLabelProps={{ ...AXIS_LABEL, textAnchor: "middle" }}
            />
          </g>
        </Group>
      </svg>
      {/* O TooltipWithBounds so se mede ao montar: a `key` monta-o de novo em cada barra, para a
          viragem junto da borda direita usar a largura do texto desta barra e nao a da anterior. */}
      {tooltipOpen && tooltipData && (
        <TooltipWithBounds key={tooltipData.key} left={tooltipLeft} top={tooltipTop} applyPositionStyle style={TOOLTIP_STYLE}>
          <strong>{tooltipData.label}</strong>
          {tooltipData.value != null && <>: {formatValue(tooltipData.value)}</>}
          {tooltipData.previous != null && (
            <>
              <br />
              {previousLabel}: {formatValue(tooltipData.previous)}
            </>
          )}
        </TooltipWithBounds>
      )}
    </div>
  );
};

export default BarChart;
